import { addDays, format } from 'date-fns';
import { toIsoDate } from './utils';

const MONTH_NAMES = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

function calendarDate(year: number, month: number, day: number): Date | null {
  if (year < 1000 || year > 9999 || month < 1 || month > 12 || day < 1) return null;
  const date = new Date(year, month - 1, day);
  const real =
    date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  return real ? date : null;
}

function isoFrom(year: number, month: number, day: number): string | null {
  const date = calendarDate(year, month, day);
  return date ? toIsoDate(date) : null;
}

function fullYear(text: string, today: Date): number {
  if (text.length !== 2) return Number(text);
  const century = Math.floor(today.getFullYear() / 100) * 100;
  const candidate = century + Number(text);
  return candidate > today.getFullYear() + 10 ? candidate - 100 : candidate;
}

function monthNumber(name: string): number | null {
  if (name.length < 3) return null;
  const index = MONTH_NAMES.findIndex((month) => month.startsWith(name));
  return index === -1 ? null : index + 1;
}

export function isoToDate(iso: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return match ? calendarDate(Number(match[1]), Number(match[2]), Number(match[3])) : null;
}

export function parseDateEntry(text: string, today: Date = new Date()): string | null {
  const input = text.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!input) return '';
  if (input === 't' || input === 'today') return toIsoDate(today);
  if (input === 'tomorrow') return toIsoDate(addDays(today, 1));
  if (input === 'yesterday') return toIsoDate(addDays(today, -1));

  const offset = /^([+-]) ?(\d{1,4})$/.exec(input);
  if (offset) return toIsoDate(addDays(today, Number(offset[2]) * (offset[1] === '-' ? -1 : 1)));

  const iso = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(input);
  if (iso) return isoFrom(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  const compact = /^(\d{2})(\d{2})(\d{2}|\d{4})$/.exec(input);
  if (compact) {
    const dayFirst = isoFrom(fullYear(compact[3], today), Number(compact[2]), Number(compact[1]));
    if (dayFirst) return dayFirst;
    const yearFirst = /^(\d{4})(\d{2})(\d{2})$/.exec(input);
    return yearFirst
      ? isoFrom(Number(yearFirst[1]), Number(yearFirst[2]), Number(yearFirst[3]))
      : null;
  }

  const numeric = /^(\d{1,2})[/.\- ](\d{1,2})(?:[/.\- ](\d{2}|\d{4}))?$/.exec(input);
  if (numeric) {
    const year = numeric[3] ? fullYear(numeric[3], today) : today.getFullYear();
    return isoFrom(year, Number(numeric[2]), Number(numeric[1]));
  }

  const dayName =
    /^(\d{1,2})(?:st|nd|rd|th)?[ \-/]?([a-z]{3,9})\.?(?:[ \-/,]+(\d{2}|\d{4}))?$/.exec(input);
  if (dayName) {
    const month = monthNumber(dayName[2]);
    const year = dayName[3] ? fullYear(dayName[3], today) : today.getFullYear();
    return month ? isoFrom(year, month, Number(dayName[1])) : null;
  }

  const nameDay =
    /^([a-z]{3,9})\.?[ \-/]?(\d{1,2})(?:st|nd|rd|th)?(?:[ \-/,]+(\d{2}|\d{4}))?$/.exec(input);
  if (nameDay) {
    const month = monthNumber(nameDay[1]);
    const year = nameDay[3] ? fullYear(nameDay[3], today) : today.getFullYear();
    return month ? isoFrom(year, month, Number(nameDay[2])) : null;
  }

  return null;
}

export function isIncompleteDate(text: string): boolean {
  const trimmed = text.trim();
  return /^\d{1,2}[/.\- ]\d{1,2}(?:[/.\- ]\d{1,3})?$/.test(trimmed) || /^\d{4,7}$/.test(trimmed);
}

export function maskDateTyping(raw: string, _previous: string): string {
  if (!/^\d/.test(raw)) return raw;
  const cleaned = raw.replace(/[^\d/.\- ]/g, '');
  const segments = cleaned.split(/[^\d]+/);
  if (segments.length > 1) {
    if (segments[0].length >= 3) return raw;
    let day = segments[0].slice(0, 2);
    if (day.length === 2 && (Number(day) > 31 || Number(day) === 0)) day = day.slice(0, 1);
    let month = segments[1].slice(0, 2);
    if (month.length === 2 && (Number(month) > 12 || Number(month) === 0)) month = month.slice(0, 1);
    let year = (segments[2] ?? '').slice(0, 4);
    if (year && !/^[12]/.test(year)) year = '';
    return [day, month, year].slice(0, segments.length).join('/');
  }
  const digits = cleaned.slice(0, 8);
  const day = digits.slice(0, 2);
  if (day.length === 2 && (Number(day) > 31 || Number(day) === 0)) return day;
  const rest = digits.slice(2);
  if (!rest) return day;
  let month: string;
  let remaining: string;
  if (rest[0] > '1') {
    month = `0${rest[0]}`;
    remaining = rest.slice(1);
  } else {
    month = rest.slice(0, 2);
    remaining = rest.slice(2);
  }
  if (month.length === 2 && (Number(month) > 12 || Number(month) === 0)) {
    month = month.slice(0, 1);
    remaining = '';
  }
  if (month.length < 2) return `${day}/${month}`;
  const year = /^[12]/.test(remaining) ? remaining.slice(0, 4) : '';
  return year ? `${day}/${month}/${year}` : `${day}/${month}`;
}

export function formatDateEntry(iso: string): string {
  const date = iso ? isoToDate(iso) : null;
  return date ? format(date, 'dd/MM/yyyy') : '';
}

export function isoWithin(iso: string, min?: string, max?: string): boolean {
  if (min && iso < min) return false;
  if (max && iso > max) return false;
  return true;
}

const pad = (value: number) => String(value).padStart(2, '0');

export function parseTimeEntry(text: string, now: Date = new Date()): string | null {
  const input = text.trim().toLowerCase().replace(/\s+/g, '');
  if (!input) return '';
  if (input === 'now') return `${pad(now.getHours())}:${pad(now.getMinutes())}`;
  if (input === 'noon') return '12:00';
  if (input === 'midnight') return '00:00';

  const meridiem = /(a\.?m?\.?|p\.?m?\.?)$/.exec(input);
  const half = meridiem ? meridiem[1][0] : undefined;
  const body = meridiem ? input.slice(0, input.length - meridiem[1].length) : input;

  let hours: number;
  let minutes: number;
  const separated = /^(\d{1,2})[:.h](\d{2})$/.exec(body);
  if (separated) {
    hours = Number(separated[1]);
    minutes = Number(separated[2]);
  } else if (/^\d{1,4}$/.test(body)) {
    hours = Number(body.length <= 2 ? body : body.slice(0, -2));
    minutes = body.length <= 2 ? 0 : Number(body.slice(-2));
  } else {
    return null;
  }

  if (minutes > 59) return null;
  if (half) {
    if (hours < 1 || hours > 12) return null;
    hours = (hours % 12) + (half === 'p' ? 12 : 0);
  } else if (hours > 23) {
    return null;
  }
  return `${pad(hours)}:${pad(minutes)}`;
}

export type HourCycle = 12 | 24;

export function formatTimeEntry(hm: string, hourCycle: HourCycle = 12): string {
  const match = /^(\d{2}):(\d{2})$/.exec(hm);
  if (!match) return '';
  const pattern = hourCycle === 24 ? 'HH:mm' : 'h:mm a';
  return format(new Date(2000, 0, 1, Number(match[1]), Number(match[2])), pattern);
}

export function shiftTime(hm: string, minutes: number): string {
  const match = /^(\d{2}):(\d{2})$/.exec(hm);
  const current = match ? Number(match[1]) * 60 + Number(match[2]) : 0;
  const next = (((current + minutes) % 1440) + 1440) % 1440;
  return `${pad(Math.floor(next / 60))}:${pad(next % 60)}`;
}

export function timeSteps(step: number): string[] {
  const safeStep = Math.max(1, Math.min(step, 720));
  return Array.from({ length: Math.ceil(1440 / safeStep) }, (_, index) =>
    shiftTime('00:00', index * safeStep),
  );
}
